import { TFunction } from "i18next";
import type { StatusColor } from "../../../../components/Status/Status";
import { AttributeSchema } from "../../../../types/types.PIM";
import type {
  ApprovalStageApprover,
  ApprovalStage,
  ApprovalWorkflow,
} from "../../../../types/types.Workflow";
import type { ApproverRow } from "./Approvers";

export function stageMatchesIdentifier(
  stage: { name: string; slug?: string | null },
  identifier?: string | null
): boolean {
  if (!identifier) {
    return false;
  }
  return stage.slug === identifier || stage.name === identifier;
}

export function findActiveApprovalStage<
  T extends { name: string; slug?: string | null; stage_status?: string }
>(stages: T[], currentStage?: string | null): T | undefined {
  const inProgress = stages.find(
    (stage) => stage.stage_status === "in_progress"
  );
  if (inProgress) {
    return inProgress;
  }
  return stages.find((stage) => stageMatchesIdentifier(stage, currentStage));
}

export function isUserApproverOnAnyStage(
  approval: Pick<ApprovalWorkflow, "stages"> | null | undefined,
  userId?: string | null
): boolean {
  if (!approval || !userId) {
    return false;
  }
  return approval.stages.some((stage) =>
    stage.approvers.some((approver) => approver.approver_id === userId)
  );
}

export function canEditApprovalAssignee(
  approval: Pick<ApprovalWorkflow, "status" | "stages"> | null | undefined,
  {
    userId,
    roleIsAdmin,
    roleIsAgilisAdmin,
  }: {
    userId?: string | null;
    roleIsAdmin: boolean;
    roleIsAgilisAdmin: boolean;
  }
): boolean {
  return (
    !!approval &&
    approval.status === "in_progress" &&
    (isUserApproverOnAnyStage(approval, userId) ||
      roleIsAdmin ||
      roleIsAgilisAdmin)
  );
}

export function approvalAssigneeDisabledTooltip(
  t: TFunction,
  {
    approval,
    canEdit,
    assigneeName,
  }: {
    approval: Pick<ApprovalWorkflow, "status"> | null | undefined;
    canEdit: boolean;
    assigneeName?: string | null;
  }
): string | undefined {
  if (!approval || canEdit) {
    return undefined;
  }
  if (approval.status === "approved" && assigneeName) {
    return t("{{name}} approved the final stage.", { name: assigneeName });
  }
  if (approval.status === "rejected" && assigneeName) {
    return t("{{name}} rejected this approval.", { name: assigneeName });
  }
  return t(
    "Only approvers on this approval and admins can change the assignee."
  );
}

export function toAssigneeOptions(
  approvers: Array<{
    approver_id: string;
    approver_name: string;
    approver_email?: string | null;
  }>
): Array<{ id: string; name: string; email?: string | null }> {
  return approvers.map((approver) => ({
    id: approver.approver_id,
    name: approver.approver_name,
    email: approver.approver_email,
  }));
}

/** Workflow + stage status badge color. */
export function approvalStatusColor(status: string): StatusColor {
  switch (status) {
    case "approved":
    case "auto_approved":
      return "green";
    case "in_progress":
      return "yellow";
    case "rejected":
      return "red";
    case "pending":
    default:
      return "gray";
  }
}

const APPROVAL_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  approved: "Approved",
  auto_approved: "Auto-Approved",
  rejected: "Rejected",
};

/** Workflow + stage status label. */
export function approvalStatusLabel(status: string, t: TFunction): string {
  const label = APPROVAL_STATUS_LABELS[status];
  return label ? t(label) : status;
}

/**
 * Which flag (if any) an approver row shows in a stage card.
 * Grey outline flag only for the assignee on an in-progress stage.
 */
export type ApproverFlagKind = "approved" | "rejected" | "assignee" | null;

export function getApproverFlagKind({
  decision,
  isStageInProgress,
  approverId,
  assigneeId,
}: {
  decision?: string | null;
  isStageInProgress: boolean;
  approverId: string;
  assigneeId?: string | null;
}): ApproverFlagKind {
  if (decision === "approve") {
    return "approved";
  }
  if (decision === "reject") {
    return "rejected";
  }
  if (
    isStageInProgress &&
    assigneeId &&
    String(approverId) === String(assigneeId)
  ) {
    return "assignee";
  }
  return null;
}

export const approvalConfirmatiomMessage = (t: TFunction) =>
  t(
    "Once submitted, this product will go through the approval workflow and you will not be able to make changes during the approval process."
  );

export const createApproverColumn = (
  id: string,
  name: string,
  inputType: "user" | "single_select",
  listId?: string | null
): AttributeSchema => {
  const hasListId = Boolean(listId);
  const isEditable = inputType !== "single_select" || hasListId;

  return {
    id,
    name,
    display_name: name,
    description: null,
    is_required: false,
    is_mandatory: false,
    is_system: false,
    is_master: false,
    is_restricted: false,
    is_visible: true,
    is_filterable: false,
    is_filter_eligible: false,
    default_values: null,
    values: [],
    list_name: null,
    list_id: (hasListId ? listId : "") as AttributeSchema["list_id"],
    object_type: "product" as any,
    new_object_type: "product",
    translations: [],
    size: "small" as const,
    icons_enabled: false,
    is_not_applicable: false,
    is_editable: isEditable,
    input_type: inputType,
    choices: [],
    is_multiple_choice: false,
  };
};

export const transformApproverData = (
  approver: ApproverRow,
  columns: AttributeSchema[]
): ApprovalStageApprover => {
  const approverColumn = columns.find((c) => c.input_type === "user");
  const approverIdValue = approverColumn
    ? approver[approverColumn.id]
    : approver.id;
  const approverNameValue = approverColumn
    ? (approver[`${approverColumn.id}_name`] as string | undefined)
    : approver.approver_name || approver.id;

  const approverId =
    typeof approverIdValue === "string" ? approverIdValue : approver.id;
  const approverName =
    typeof approverNameValue === "string" ? approverNameValue : approver.id;

  const result: ApprovalStageApprover = {
    approver_id: approverId,
    approver_name: approverName,
    approver_email: "",
    approver_status: "pending",
    approver_comment: "",
    approver_decision: "",
  };

  for (const column of columns) {
    if (column.input_type === "user") {
      continue;
    }
    const value = approver[column.id];
    result[column.id] = typeof value === "string" && value !== "-" ? value : "";
  }

  return result;
};

export const transformSectionToStage = (
  title: string,
  data: ApproverRow[],
  columns: AttributeSchema[]
): ApprovalStage => ({
  stage_name: title,
  approvers: data
    .filter((row) => !row.edit)
    .map((approver) => transformApproverData(approver, columns)),
});

export const getApproverSectionKey = (
  stageName: string,
  index: number
): string => {
  const normalized = stageName.toLowerCase().replace(/\s+/g, "_");
  return normalized || `stage_${index}`;
};

export const getApproverPersonColumnTitle = (
  stageTitle: string,
  t: TFunction
): string => {
  if (stageTitle === "Principal") {
    return t("Principal Manager");
  }
  if (stageTitle === "Regulatory") {
    return t("Regulatory Approver");
  }
  if (stageTitle === "Master Data") {
    return t("Master Data Approver");
  }
  return t("Approver");
};
